import { supabase, publicSupabase } from '@/integrations/supabase/client';
import { 
  CreateBlogDto, 
  UpdateBlogDto, 
  CreateBlogCategoryDto, 
  UpdateBlogCategoryDto 
} from '@/types/blog';

// API base URL for the Edge Function
const getApiUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // For local development, determine if we should use local Supabase Functions emulator
  // or fall back to the production URL
  if (isLocalhost) {
    // Use local Supabase Functions emulator if VITE_USE_LOCAL_FUNCTIONS is set,
    // otherwise use the production URL for development
    const useLocalFunctions = import.meta.env.VITE_USE_LOCAL_FUNCTIONS === 'true';
    if (useLocalFunctions) {
      return 'http://localhost:54321/functions/v1/blog-api';
    }
  }
  
  // Default to production URL
  return `https://jftsemrnjqbzinphyrpm.supabase.co/functions/v1/blog-api`;
};

// Ensure auth token is valid and refresh if needed
const ensureValidAuthToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.warn('No active session found');
      return false;
    }
    
    // Check if token expires soon (within 5 minutes)
    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0; // convert to ms
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (expiresAt && expiresAt - now < fiveMinutes) {
      console.log('Auth token expires soon, refreshing...');
      const { data: refreshResult, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('Error refreshing session:', refreshError);
        return false;
      }
      
      return !!refreshResult.session;
    }
    
    return true;
  } catch (error) {
    console.error('Error ensuring valid auth token:', error);
    return false;
  }
};

// Helper to make authenticated requests to our Edge Function
const makeAuthenticatedRequest = async (endpoint: string, options: RequestInit = {}) => {
  // Ensure token is valid before proceeding
  const isValidToken = await ensureValidAuthToken();
  if (!isValidToken) {
    console.error('Failed to ensure valid authentication token');
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    console.error('Authentication error: No access token available. Please sign in again.');
    throw new Error('Authentication error: You must be signed in to access this feature.');
  }

  try {
    // Make sure the endpoint doesn't start with a slash
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    
    const url = `${getApiUrl()}/${cleanEndpoint}`;
    console.log(`Making request to: ${url}`);
    
    // Get the Supabase anon key from the environment
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
    
    if (!supabaseAnonKey) {
      console.warn('Warning: VITE_SUPABASE_ANON_KEY is not set. Authentication might fail.');
    }
    
    const headers = {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': supabaseAnonKey,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };
    
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for additional auth context
      mode: 'cors', // Explicitly set CORS mode
    });

    if (!response.ok) {
      // Try to parse detailed error information
      let errorMessage = `HTTP error ${response.status}`;
      let errorDetails = {};
      
      try {
        const errorData = await response.json();
        errorDetails = errorData;
        errorMessage = errorData.error || errorMessage;
        
        // Log detailed error information
        console.error('API error response:', {
          status: response.status,
          statusText: response.statusText,
          url,
          errorData,
        });
      } catch (jsonError) {
        console.error('Failed to parse error response:', jsonError);
      }
      
      // Handle specific error codes with helpful messages
      if (response.status === 401) {
        throw new Error('Authentication failed: Your session might have expired. Please sign in again.');
      } else if (response.status === 403) {
        throw new Error('Access denied: You do not have permission to perform this action.');
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    console.error('Network or API error:', error);
    throw error;
  }
};

// Interface for nested category structure
interface CategoryMapping {
  category: {
    id: string;
    name: string;
    slug: string;
    description?: string;
  };
}

// Read Blog Post Endpoints (public access)
export const getBlogs = async (page = 1, pageSize = 10, status?: string, categoryId?: string | null, sort = 'desc') => {
  try {
    // Always use the authenticated client to make sure drafts are accessible
    // When specifically requesting published blogs, we could use public client,
    // but for simplicity and to avoid issues, we'll use the auth client for all requests
    const client = supabase;
    console.log(`Fetching blogs with authenticated Supabase client`);
    
    // Calculate pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Start with a query for blogs
    let query = client
      .from('blogs')
      .select(`
        *,
        categories:blog_category_mappings(
          category:blog_categories(id, name, slug, description)
        )
      `, { count: 'exact' })
      .order('created_at', { ascending: sort === 'asc' });
      
    // Add status filter if provided
    if (status) {
      console.log('Filtering blogs by status:', status);
      query = query.eq('status', status);
    }
    
    // Add category filter if needed
    if (categoryId) {
      // For category filter, we need to join through the junction table
      query = query.filter('categories.category.id', 'eq', categoryId);
    }
    
    // Add pagination
    query = query.range(from, to);
    
    // Execute the query
    const { data, count, error } = await query;
    
    if (error) {
      console.error('Error fetching blogs:', error);
      throw error;
    }
    
    console.log(`Found ${data?.length || 0} blogs with status: ${status || 'all'}`);
    
    // Transform the data structure to match the expected format
    const transformedData = data?.map(blog => {
      // Extract categories from the nested structure
      const categories = blog.categories?.map((item: CategoryMapping) => item.category) || [];
      return {
        ...blog,
        categories
      };
    });
    
    return {
      data: transformedData || [],
      pagination: {
        page,
        pageSize,
        totalItems: count || 0,
        totalPages: count ? Math.ceil(count / pageSize) : 0
      }
    };
  } catch (error) {
    console.error('Error in getBlogs:', error);
    throw error;
  }
};

export const getBlogBySlug = async (slug: string) => {
  try {
    console.log('BlogApi: Fetching blog by slug with Supabase authenticated client');
    console.log('BlogApi: Slug being searched for:', slug);
    
    // Check if we have a valid session before proceeding
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('BlogApi: Current session:', sessionData?.session ? 'Valid session' : 'No session');
    
    // First try with authenticated client - this should allow access to drafts
    let query = supabase
      .from('blogs')
      .select(`
        *,
        categories:blog_category_mappings(
          category:blog_categories(id, name, slug, description)
        )
      `)
      .eq('slug', slug)
      .single();
    
    let { data, error } = await query;
    
    if (error) {
      console.error('BlogApi: Error fetching blog with auth:', error);
      
      // If auth fails, try public client as fallback but only for published posts
      const { data: publicData, error: publicError } = await publicSupabase
        .from('blogs')
        .select(`
          *,
          categories:blog_category_mappings(
            category:blog_categories(id, name, slug, description)
          )
        `)
        .eq('status', 'published')
        .eq('slug', slug)
        .single();
        
      if (publicError) {
        console.error('BlogApi: Error fetching blog with public client:', publicError);
        throw publicError;
      }
      
      data = publicData;
    }
    
    console.log('BlogApi: Blog fetch result:', data ? 'Blog found' : 'No blog found');
    if (data) {
      console.log('BlogApi: Blog status:', data.status);
    }
    
    // Transform the data structure to match the expected format
    if (data) {
      const categories = data.categories?.map((item: CategoryMapping) => item.category) || [];
      return {
        ...data,
        categories
      };
    }
    
    return data;
  } catch (error) {
    console.error('BlogApi: Error in getBlogBySlug:', error);
    throw error;
  }
};

export const getBlogById = async (id: string) => {
  try {
    console.log('BlogApi: Fetching blog by ID with Supabase authenticated client');
    console.log('BlogApi: ID being searched for:', id);
    
    // Check if we have a valid session before proceeding
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('BlogApi: Current session:', sessionData?.session ? 'Valid session' : 'No session');
    
    // First try with authenticated client
    let query = supabase
      .from('blogs')
      .select(`
        *,
        categories:blog_category_mappings(
          category:blog_categories(id, name, slug, description)
        )
      `)
      .eq('id', id)
      .single();
    
    const { data, error } = await query;
    
    if (error) {
      console.error('BlogApi: Error fetching blog by ID:', error);
      throw error;
    }
    
    console.log('BlogApi: Blog fetch result by ID:', data ? 'Blog found' : 'No blog found');
    if (data) {
      console.log('BlogApi: Blog status:', data.status);
      
      // Transform the data structure to match the expected format
      const categories = data.categories?.map((item: CategoryMapping) => item.category) || [];
      return {
        ...data,
        categories
      };
    }
    
    return data;
  } catch (error) {
    console.error('BlogApi: Error in getBlogById:', error);
    throw error;
  }
};

// Helper function to implement retry logic for API calls
const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries = 2,
  delayMs = 1000
): Promise<T> => {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Wait before retrying, but not on the first attempt
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt} of ${maxRetries}...`);
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
      
      return await requestFn();
    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed:`, error);
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Don't retry 4xx errors (client errors)
      if (error instanceof Error && error.message.includes('4')) {
        throw error;
      }
    }
  }
  
  throw lastError || new Error('Request failed after retries');
};

// Write operations - Keep using authenticated functions
export const createBlog = async (blogData: CreateBlogDto, authorId: string) => {
  // For localhost, use direct Supabase client to avoid CORS issues with Edge Functions
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('Using direct Supabase client for blog creation on localhost');
    try {
      // Extract category_ids from blogData
      const { category_ids, ...blogDetails } = blogData;
      
      // Add author_id and timestamps
      const fullBlogData = {
        ...blogDetails,
        author_id: authorId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        published_at: blogData.status === 'published' ? new Date().toISOString() : null
      };
      
      // Insert the blog post
      const { data: insertedBlog, error: blogError } = await supabase
        .from('blogs')
        .insert(fullBlogData)
        .select('*')
        .single();
        
      if (blogError) throw blogError;
      
      // If categories were provided, create mappings
      if (insertedBlog && insertedBlog.id && category_ids && category_ids.length > 0) {
        const mappings = category_ids.map((categoryId: string) => ({
          blog_id: insertedBlog.id,
          category_id: categoryId
        }));
        
        const { error: mappingError } = await supabase
          .from('blog_category_mappings')
          .insert(mappings);
          
        if (mappingError) {
          console.error('Error creating category mappings:', mappingError);
        }
      }
      
      return insertedBlog;
    } catch (error) {
      console.error('Error creating blog with direct Supabase client:', error);
      throw error;
    }
  }
  
  // For production, use the Edge Function API with retry logic
  return retryRequest(() => makeAuthenticatedRequest('/blogs', {
    method: 'POST',
    body: JSON.stringify(blogData),
  }));
};

export const updateBlog = async (id: string, blogData: UpdateBlogDto) => {
  // For localhost, use direct Supabase client to avoid CORS issues with Edge Functions
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('Using direct Supabase client for blog update on localhost');
    try {
      // Add updated timestamp
      const { category_ids, ...blogDetails } = blogData;
      const updateData: any = {
        ...blogDetails,
        updated_at: new Date().toISOString()
      };
      
      // If status is changing to published, set published_at date
      if (blogData.status === 'published') {
        // Check if currently published
        const { data: existingBlog } = await supabase
          .from('blogs')
          .select('status, published_at')
          .eq('id', id)
          .single();

        if (existingBlog?.status !== 'published') {
          updateData.published_at = new Date().toISOString();
        }
      }
      
      // Update the blog post
      const { data: updatedBlog, error: blogError } = await supabase
        .from('blogs')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
        
      if (blogError) throw blogError;
      
      // Update category mappings if provided
      if (updatedBlog && category_ids !== undefined) {
        // First delete existing mappings
        const { error: deleteError } = await supabase
          .from('blog_category_mappings')
          .delete()
          .eq('blog_id', id);
          
        if (deleteError) {
          console.error('Error deleting existing category mappings:', deleteError);
        }
        
        // Then create new mappings if there are categories
        if (category_ids && category_ids.length > 0) {
          const mappings = category_ids.map((categoryId: string) => ({
            blog_id: id,
            category_id: categoryId
          }));
          
          const { error: mappingError } = await supabase
            .from('blog_category_mappings')
            .insert(mappings);
            
          if (mappingError) {
            console.error('Error creating new category mappings:', mappingError);
          }
        }
      }
      
      return updatedBlog;
    } catch (error) {
      console.error('Error updating blog with direct Supabase client:', error);
      throw error;
    }
  }
  
  // For production, use the Edge Function API with retry logic
  return retryRequest(() => makeAuthenticatedRequest(`/blogs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(blogData),
  }));
};

export const deleteBlog = async (id: string) => {
  return makeAuthenticatedRequest(`/blogs/${id}`, {
    method: 'DELETE',
  });
};

// Blog Category Endpoints
export const getCategories = async () => {
  try {
    console.log('Fetching categories with Supabase public client');
    
    // Query for all categories
    const { data, error } = await publicSupabase
      .from('blog_categories')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getCategories:', error);
    throw error;
  }
};

export const getCategoryBySlug = async (slug: string) => {
  try {
    const { data, error } = await publicSupabase
      .from('blog_categories')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) {
      console.error('Error fetching category by slug:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getCategoryBySlug:', error);
    throw error;
  }
};

// Write operations for categories - Keep using authenticated functions
export const createCategory = async (categoryData: CreateBlogCategoryDto) => {
  // For localhost, use direct Supabase client to avoid CORS issues with Edge Functions
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('Using direct Supabase client for category creation on localhost');
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .insert(categoryData)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating category with direct Supabase client:', error);
      throw error;
    }
  }
  
  // For production, use the Edge Function API with retry logic
  return makeAuthenticatedRequest('/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  });
};

export const updateCategory = async (id: string, categoryData: UpdateBlogCategoryDto) => {
  // For localhost, use direct Supabase client to avoid CORS issues with Edge Functions
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('Using direct Supabase client for category update on localhost');
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .update(categoryData)
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating category with direct Supabase client:', error);
      throw error;
    }
  }
  
  // For production, use the Edge Function API with retry logic
  return makeAuthenticatedRequest(`/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  });
};

export const deleteCategory = async (id: string) => {
  // For localhost, use direct Supabase client to avoid CORS issues with Edge Functions
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('Using direct Supabase client for category deletion on localhost');
    try {
      const { error } = await supabase
        .from('blog_categories')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting category with direct Supabase client:', error);
      throw error;
    }
  }
  
  // For production, use the Edge Function API with retry logic
  return makeAuthenticatedRequest(`/categories/${id}`, {
    method: 'DELETE',
  });
};

// Helper Functions - Keep using authenticated functions
export const generateUniqueSlug = async (title: string, existingId?: string) => {
  if (!title) {
    return { slug: '' };
  }
  
  try {
    // For localhost always use client-side slug generation to avoid CORS errors
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('Using client-side slug generation for localhost');
      return clientSideGenerateSlug(title, existingId);
    }

    // For production, try the API first
    try {
      const result = await makeAuthenticatedRequest('/generate-slug', {
        method: 'POST',
        body: JSON.stringify({ title, existingId }),
      });
      return result;
    } catch (apiError) {
      console.error('Error generating slug via API:', apiError);
      // Fall back to client-side generation
      console.log('Falling back to client-side slug generation');
      return clientSideGenerateSlug(title, existingId);
    }
  } catch (error) {
    console.error('Error in slug generation:', error);
    
    // Final fallback - just create a basic slug
    const baseSlug = title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .trim(); // Remove whitespace from both ends
      
    return { slug: baseSlug };
  }
};

// Client-side fallback for slug generation when API is unavailable
const clientSideGenerateSlug = async (title: string, existingId?: string): Promise<{ slug: string }> => {
  if (!title) {
    throw new Error('Title is required for slug generation');
  }

  // Generate a base slug from the title
  let baseSlug = title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    .trim(); // Remove whitespace from both ends

  let slug = baseSlug;
  let counter = 0;
  let slugExists = true;

  // Keep checking until we find a unique slug
  while (slugExists && counter < 100) { // Add a max limit to prevent infinite loops
    // Check if slug exists in the blogs table
    const query = publicSupabase
      .from('blogs')
      .select('id')
      .eq('slug', slug);
    
    // If updating an existing blog, exclude it from the check
    const { data, error } = existingId 
      ? await query.neq('id', existingId)
      : await query;
    
    if (error) {
      console.error('Error checking slug uniqueness:', error);
      throw error;
    }

    // If no data, we have a unique slug
    if (!data || data.length === 0) {
      slugExists = false;
    } else {
      // Increment counter and try a new slug
      counter++;
      slug = `${baseSlug}-${counter}`;
    }
  }

  return { slug };
};

// Helper function to check permissions - for debugging purposes
export const checkBlogPermissions = async (slug: string) => {
  try {
    console.log('Checking permissions for blog with slug:', slug);
    
    // Check if we have a valid session
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('Current session:', sessionData?.session ? 'Valid' : 'None');
    
    // Try public access first
    const { data: publicData, error: publicError } = await publicSupabase
      .from('blogs')
      .select('id, title, slug, status')
      .eq('slug', slug)
      .single();
      
    console.log('Public access result:', publicError ? 'Error' : (publicData ? 'Found' : 'Not found'));
    if (publicError) console.log('Public access error:', publicError.message);
    if (publicData) console.log('Public access data:', publicData);
    
    // Try authenticated access
    const { data: authData, error: authError } = await supabase
      .from('blogs')
      .select('id, title, slug, status')
      .eq('slug', slug)
      .single();
      
    console.log('Auth access result:', authError ? 'Error' : (authData ? 'Found' : 'Not found'));
    if (authError) console.log('Auth access error:', authError.message);
    if (authData) console.log('Auth access data:', authData);
    
    return {
      publicAccess: !publicError && !!publicData,
      authAccess: !authError && !!authData,
      publicData,
      authData,
      publicError,
      authError
    };
  } catch (error) {
    console.error('Error checking permissions:', error);
    return {
      publicAccess: false,
      authAccess: false,
      error
    };
  }
}; 